import React, { useEffect, useMemo, useState } from "react";
import { Card, Collapse, Input, Button, message, Spin } from "antd";
import _ from "lodash";
import axios from "../../../core_units/api/axios-config";
import { connect } from "react-redux";

const TeachingCuesManager = ({ coachProfile }) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingIds, setSavingIds] = useState({});
  const [drafts, setDrafts] = useState({});

  const schoolId = _.get(coachProfile, "school.id");

  const loadSeries = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/schools/${schoolId}/teaching-cues`, { withCredentials: true });
      const list = _.get(res, "data.series", []);
      setSeries(list);
    } catch (_err) {
      message.error("Unable to load teaching cues.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSeries();
  }, [schoolId]);

  const onDraftChange = (cueId, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [cueId]: {
        ...(prev[cueId] || {}),
        [key]: value
      }
    }));
  };

  const saveCue = async (cue) => {
    const payload = drafts[cue.id];
    if (!payload) return;
    setSavingIds((prev) => ({ ...prev, [cue.id]: true }));
    try {
      await axios.patch(`/api/teaching-cues/${cue.id}`, payload, { withCredentials: true });
      message.success("Cue updated.");
      await loadSeries();
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[cue.id];
        return next;
      });
    } catch (_err) {
      message.error("Unable to update cue.");
    } finally {
      setSavingIds((prev) => ({ ...prev, [cue.id]: false }));
    }
  };

  const panels = useMemo(
    () =>
      series.map((item) => ({
        key: String(item.id),
        label: `Game #${item.gameId} - ${item.status} (${item.cueCount || item.cues?.length || 0} cues)`,
        children: (
          <div className="flex flex-col gap-3">
            {(item.cues || []).map((cue) => {
              const draft = drafts[cue.id] || {};
              return (
                <div key={cue.id} className="border border-[#EAEDF0] rounded p-3">
                  <div className="text-[12px] text-[#4A6074] mb-2">
                    Play {cue.playId} • {cue.positionGroup}
                  </div>
                  <Input
                    value={draft.primaryCue ?? cue.primaryCue}
                    onChange={(e) => onDraftChange(cue.id, "primaryCue", e.target.value)}
                    placeholder="Primary cue (3 words)"
                    className="mb-2"
                  />
                  <Input
                    value={draft.secondaryCue ?? cue.secondaryCue}
                    onChange={(e) => onDraftChange(cue.id, "secondaryCue", e.target.value)}
                    placeholder="Secondary cue"
                    className="mb-2"
                  />
                  <Input.TextArea
                    value={draft.explanation ?? cue.explanation}
                    onChange={(e) => onDraftChange(cue.id, "explanation", e.target.value)}
                    placeholder="Brief explanation"
                    rows={2}
                    className="mb-2"
                  />
                  <Button
                    type="primary"
                    size="small"
                    loading={!!savingIds[cue.id]}
                    onClick={() => saveCue(cue)}
                  >
                    Save
                  </Button>
                </div>
              );
            })}
          </div>
        )
      })),
    [series, drafts, savingIds]
  );

  return (
    <div className="w-[98%] p-[2%]">
      <h3 className="text-[24px] font-bold mb-3">Teaching Cue Audit</h3>
      <Card>
        {loading ? <Spin /> : <Collapse items={panels} />}
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  coachProfile: _.get(state, "app.account.profile.data.details")
});

export default connect(mapStateToProps)(TeachingCuesManager);
